import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useEffect} from "react";
import {
	AttributionControl,
	CircleMarker,
	LayersControl,
	MapContainer,
	Marker,
	Popup,
	TileLayer,
	useMap
} from "react-leaflet";

import Button from "Components/Button.js";

import "Raildays/Components/RaildaysLeaflet.js";

const MAPBOX_TOKEN = "pk.eyJ1IjoiYWxhbi1qb2huc29uLWhvcHMiLCJhIjoiY2p0aXE0MmdsMmp5cTQ0cDh6Mm4xNXZwYiJ9.MSPHlyfGV5d7PT_5Imz1Qg";
const MAPBOX_URL = "https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}";
const MAP_ATTRIBUTION = "Map data © OpenStreetMap contributors, CC-BY-SA, Imagery © Mapbox";

const mapLayers = [
	{name: "Map View", id: "alan-johnson-hops/ckr0iay7j0mqw17le5dimgqmm"},
	{name: "Outdoor View", id: "alan-johnson-hops/ckr0m6rosbv1918mkm3lk9990"},
	{name: "Satellite View", id: "alan-johnson-hops/ckr0mizdu6w4s17mcg1xlxbai"}
];

const resolvePopupActionLabel = marker => {
	if (marker?.actionLabel) return marker.actionLabel;
	const link = marker?.link || "";
	if (link.includes("/railways/")) return "View railway";
	if (link.includes("/events/")) return "View event";
	if (link.includes("/experiences/")) return "View experience";
	return "View information";
};

const MapPopupContent = ({marker}) => {
	const name = marker?.name || "";
	const railwayName = marker?.railwayName || "";
	const location = marker?.location || "";
	const dateLabel = marker?.dateLabel || "";
	const typeLabel = marker?.typeLabel || "";
	const distanceLabel = marker?.distanceLabel || "";
	const fallbackSubname = marker?.subname || "";

	return (
		<Stack spacing={0.8} sx={{minWidth: 200, textAlign: "center", alignItems: "center"}}>
			{marker?.image && (
				<Box
					component="img"
					alt=""
					src={marker.image}
					sx={{height: 42, width: "auto", objectFit: "contain"}} />
			)}
			<Typography variant="subtitle1" sx={{fontWeight: 800, textAlign: "center", lineHeight: 1.25}}>
				{name}
			</Typography>
			{railwayName && (railwayName !== name) && (
				<Typography variant="subtitle2" sx={{textAlign: "center", fontWeight: 800, color: "#334155", lineHeight: 1.2}}>
					{railwayName}
				</Typography>
			)}
			{location && (
				<Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
					{location}
				</Typography>
			)}
			{!railwayName && !location && fallbackSubname && (
				<Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
					{fallbackSubname}
				</Typography>
			)}
			{dateLabel && (
				<Typography variant="caption" sx={{textAlign: "center", color: "#334155", fontWeight: 700}}>
					{dateLabel}
				</Typography>
			)}
			{typeLabel && (
				<Typography variant="caption" sx={{textAlign: "center", color: "#475569"}}>
					{typeLabel}
				</Typography>
			)}
			{distanceLabel && (
				<Typography variant="caption" sx={{textAlign: "center", color: "#334155", fontWeight: 700}}>
					{distanceLabel}
				</Typography>
			)}
			{marker?.link && (
				<Button
					label={resolvePopupActionLabel(marker)}
					uri={marker.link}
					variant="contained"
					size="small" />
			)}
		</Stack>
	);
};

const MapEffects = ({bounds, mapRef}) => {
	const map = useMap();

	useEffect(() => {
		if (mapRef) mapRef.current = map;
		const timer = window.setTimeout(() => {
			map.invalidateSize();
		}, 100);
		return () => window.clearTimeout(timer);
	}, [map, mapRef]);

	useEffect(() => {
		if (bounds && bounds.length) {
			map.fitBounds(bounds, {padding: [50, 50]});
		}
	}, [bounds, map]);

	return null;
};

const RaildaysMap = ({
	markers = [],
	bounds,
	center,
	zoom,
	height,
	mapRef,
	markerRefs,
	markerShape,
	legendItems = []
}) => {
	const resolvedShape = markerShape || "circle";

	const mapCenter = center || [54.5, -2];
	const mapZoom = zoom || 6;

	return (
		<Box sx={{height: height || 520, width: "100%", minHeight: 360, overflow: "hidden", borderRadius: 2, position: "relative"}}>
			<GlobalStyles
				styles={{
					".leaflet-container": {
						font: "inherit"
					},
					".leaflet-div-icon": {
						background: "transparent",
						border: "none"
					},
					".raildays-map-marker": {
						background: "transparent",
						border: "none"
					},
					".leaflet-popup-content-wrapper, .leaflet-popup-tip": {
						background: "#fff",
						color: "#111827",
						boxShadow: "0 12px 30px rgba(15, 23, 42, 0.25)",
						opacity: 1
					},
					".leaflet-popup-content": {
						margin: "12px 16px"
					},
					".leaflet-popup": {
						maxWidth: "320px"
					},
					".leaflet-control-layers": {
						fontFamily: "inherit"
					}
				}}
			/>
			<MapContainer
				attributionControl={false}
				bounds={bounds && bounds.length ? bounds : undefined}
				boundsOptions={{padding: [50, 50]}}
				center={mapCenter}
				zoom={mapZoom}
				style={{height: "100%", width: "100%", fontFamily: "inherit"}}
				zoomSnap={0.25}
				zoomDelta={0.5}>
				<AttributionControl prefix="Leaflet" />
				<MapEffects bounds={bounds} mapRef={mapRef} />
				<LayersControl position="topright">
					{mapLayers.map((layer, index) => (
						<LayersControl.BaseLayer key={layer.id} checked={index === 0} name={layer.name}>
							<TileLayer
								attribution={MAP_ATTRIBUTION}
								url={MAPBOX_URL}
								id={layer.id}
								accessToken={MAPBOX_TOKEN}
								tileSize={512}
								zoomOffset={-1} />
						</LayersControl.BaseLayer>
					))}
				</LayersControl>

				{markers.map((marker, markerIndex) => {
					const markerKey = `${marker?.id || marker?.name || "marker"}-${marker?.position?.[0] || 0}-${marker?.position?.[1] || 0}-${markerIndex}`;
					if (resolvedShape === "pin") {
						return (
							<Marker
								key={markerKey}
								position={marker.position}
								ref={instance => {
									if (markerRefs && instance && marker?.id !== undefined) markerRefs.current[marker.id] = instance;
								}}>
								<Popup autoPan>
									<MapPopupContent marker={marker} />
								</Popup>
							</Marker>
						);
					}

					return (
						<CircleMarker
							key={markerKey}
							center={marker.position}
							radius={7}
							ref={instance => {
								if (markerRefs && instance && marker?.id !== undefined) markerRefs.current[marker.id] = instance;
							}}
							pathOptions={{
								color: marker.color || "#444",
								fillColor: marker.color || "#444",
								fillOpacity: 0.9,
								weight: 2
							}}>
							<Popup autoPan>
								<MapPopupContent marker={marker} />
							</Popup>
						</CircleMarker>
					);
				})}
			</MapContainer>
			{legendItems.length > 0 && (
				<Box
					sx={{
						position: "absolute",
						left: 12,
						bottom: 24,
						zIndex: 500,
						backgroundColor: "rgba(255,255,255,0.94)",
						border: "1px solid rgba(148, 163, 184, 0.5)",
						borderRadius: 1.5,
						boxShadow: "0 8px 20px rgba(15, 23, 42, 0.18)",
						p: 1.25,
						minWidth: 140
					}}>
					<Typography variant="caption" sx={{display: "block", fontWeight: 800, mb: 0.5, color: "#334155"}}>
						Legend
					</Typography>
					<Stack spacing={0.5} sx={{maxHeight: 220, overflowY: "auto", pr: 0.25}}>
						{legendItems.map((item, itemIndex) => (
							<Stack key={`${item.label || "legend"}-${item.color || "default"}-${itemIndex}`} direction="row" spacing={0.75} alignItems="center">
								<Box
									sx={{
										width: 10,
										height: 10,
										borderRadius: "50%",
										backgroundColor: item.color || "#444",
										border: "1px solid rgba(15, 23, 42, 0.3)",
										flexShrink: 0
									}} />
								<Typography variant="caption" sx={{color: "#0f172a", lineHeight: 1.2}}>
									{item.label}
								</Typography>
							</Stack>
						))}
					</Stack>
				</Box>
			)}
		</Box>
	);
};

export default RaildaysMap;
